import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { IoAddOutline, IoArrowBackOutline } from "react-icons/io5";
import { getPage } from "../../../api/pagesApi";
import {
  createSection,
  deleteSection,
  listSections,
  updateSection,
} from "../../../api/sectionsApi";
import { getApiErrorMessage } from "../../../api/unwrapApiResponse";
import { isServicePageSlug } from "../../../config/cmsServicePages";
import { isIndustryPageSlug } from "../../../config/cmsIndustryPages";
import ConfirmDialog from "../../../components/dashboard/ConfirmDialog";
import DashboardPanel from "../../../components/dashboard/DashboardPanel";
import EmptyState from "../../../components/dashboard/EmptyState";
import ErrorState from "../../../components/dashboard/ErrorState";
import LoadingState from "../../../components/dashboard/LoadingState";
import { useNotify } from "../../../components/ui/NotificationProvider";
import SectionFormModal from "./SectionFormModal";
import SectionList from "./SectionList";

export default function SectionsManager() {
  const { pageId } = useParams();
  const notify = useNotify();
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [pageData, sectionData] = await Promise.all([
        getPage(pageId),
        listSections(pageId),
      ]);
      setPage(pageData);
      const list = Array.isArray(sectionData) ? sectionData : [];
      setSections(list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [pageId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleSubmit(payload) {
    setBusy(true);
    try {
      if (editing?.id) {
        await updateSection(editing.id, payload);
        notify.success("Section updated");
      } else {
        await createSection(pageId, payload);
        notify.success("Section created");
      }
      setModalOpen(false);
      setEditing(null);
      await loadData();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteSection(deleteTarget.id);
      notify.success("Section deleted");
      setDeleteTarget(null);
      await loadData();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleTogglePublish(section) {
    setBusy(true);
    try {
      await updateSection(section.id, {
        name: section.name,
        key: section.key,
        sectionType: section.sectionType,
        isPublished: !section.isPublished,
        sortOrder: section.sortOrder,
      });
      notify.success(section.isPublished ? "Section unpublished" : "Section published");
      await loadData();
    } catch (err) {
      notify.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <LoadingState message="Loading sections…" />;
  if (error) return <ErrorState message={error} onRetry={loadData} />;

  const backTo = isServicePageSlug(page?.slug)
    ? "/cmsadmin/dashboard/services"
    : isIndustryPageSlug(page?.slug)
      ? "/cmsadmin/dashboard/industries"
      : "/cmsadmin/dashboard/pages";
  const backLabel = isServicePageSlug(page?.slug)
    ? "Back to service pages"
    : isIndustryPageSlug(page?.slug)
      ? "Back to industry pages"
      : "Back to pages";

  return (
    <div className="space-y-6">
      <Link
        to={backTo}
        className="inline-flex items-center gap-2 text-sm text-slate-500 transition hover:text-slate-900"
      >
        <IoArrowBackOutline className="h-4 w-4" />
        {backLabel}
      </Link>

      <DashboardPanel
        noPadding
        bodyClassName=""
        title={`${page?.name || "Page"} sections`}
        description="Each block below is a content section on the live page. Open a section to edit its fields."
        badge={
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
            {sections.length} section{sections.length === 1 ? "" : "s"}
          </span>
        }
      >
        <div className="flex flex-col gap-3 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="font-mono text-xs text-slate-500">/{page?.slug}</p>
          <button
            type="button"
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white"
          >
            <IoAddOutline className="h-5 w-5" />
            Add section
          </button>
        </div>
      </DashboardPanel>

      {sections.length === 0 ? (
        <EmptyState
          title="No sections yet"
          description="Add sections to manage this page's content."
          action={
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              Add section
            </button>
          }
        />
      ) : (
        <SectionList
          sections={sections}
          onEdit={(section) => {
            setEditing(section);
            setModalOpen(true);
          }}
          onDelete={setDeleteTarget}
          onTogglePublish={handleTogglePublish}
        />
      )}

      <SectionFormModal
        open={modalOpen}
        initial={editing}
        busy={busy}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete section?"
        message={
          deleteTarget
            ? `This will permanently delete "${deleteTarget.name}".`
            : ""
        }
        busy={busy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
